<template>
  <AuthLayout
    sidebar-title="Verify it is you"
    sidebar-text="Enter the 6-digit code we sent. It helps keep your health information safe and secure."
    :support-phone="supportPhone"
    :is-admin="isAdmin"
    title="Enter verification code"
    :subtitle="`We sent a code to your ${verificationDestination}.`"
  >
    <form action="javascript:void(0)" @submit.prevent="handleSubmit">
      <OtpInput v-model="code" :error="error" />

      <div v-if="showTrustOption" class="auth-login__row-between" style="margin-top: 16px">
        <AuthCheckbox id="remember-device-verify" v-model="rememberDevice" label="Remember for 90 days" />
      </div>

      <div class="auth-login__actions">
        <AuthButton type="submit" :loading="loading" :disabled="code.length < 6">
          Verify
        </AuthButton>
        <AuthButton
          variant="ghost"
          :loading="loading"
          :disabled="isCoolingDown"
          @click="emit('resend')"
        >
          {{ isCoolingDown ? `Resend code in ${countdown}` : 'Resend code' }}
        </AuthButton>
      </div>
    </form>

    <p v-if="isCoolingDown" class="auth-login__cooldown-hint">
      Please wait {{ countdown }} before requesting another code.
    </p>

    <button type="button" class="auth-login__back" :disabled="loading" @click="emit('cancel')">
      ← Back
    </button>
  </AuthLayout>
</template>

<script setup>
import { computed, shallowRef, watch } from 'vue'
import { MFA_METHOD } from '../constants/steps.js'
import AuthLayout from '../../shared/components/AuthLayout.vue'
import AuthButton from '../../shared/components/AuthButton.vue'
import AuthCheckbox from '../../shared/components/AuthCheckbox.vue'
import OtpInput from '../../shared/components/OtpInput.vue'

const props = defineProps({
  method:           { type: Number, required: true },
  showTrustOption:  { type: Boolean, default: false },
  trustDevice:      { type: Boolean, default: false },
  loading:          { type: Boolean, default: false },
  error:            { type: String,  default: '' },
  isCoolingDown:    { type: Boolean, default: false },
  countdown:        { type: String,  default: '1:00' },
  supportPhone:     { type: String,  default: '815-908-2020' },
  isAdmin:          { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'resend', 'cancel', 'clear-error'])

const code = shallowRef('')
const rememberDevice = shallowRef(props.trustDevice)
const isResettingInvalidCode = shallowRef(false)
const verificationDestination = computed(() => (
  Number(props.method) === MFA_METHOD.SMS ? 'mobile device' : 'email'
))

watch(code, () => {
  if (isResettingInvalidCode.value) return
  emit('clear-error')
}, { flush: 'sync' })

watch(() => props.error, (error) => {
  if (!error || !code.value) return

  isResettingInvalidCode.value = true
  code.value = ''
  isResettingInvalidCode.value = false
}, { flush: 'sync' })

const handleSubmit = () => {
  if (code.value.length < 6) return
  emit('submit', { code: code.value, trust: rememberDevice.value })
}
</script>
